import { toTwoDecimals } from "@/lib/picos/numberFormat";

type Positioned = { position?: number | null };
type Dated = { earned?: boolean | null, completionDate?: Date | null };

/**
 * The position for a new item: one past the largest position in use, or 1 when none is in use.
 * A missing, null or non-finite position counts as 0. (OWA-1594)
 */
export function nextPosition(items: ReadonlyArray<Positioned>): number {
  let max = 0;

  for (const item of items) {
    const p = item.position;
    if (typeof p === "number" && Number.isFinite(p) && p > max) max = p;
  }

  return max + 1;
}

/**
 * A milestone's percentage of its flat fee on the 0 to 100 scale the percentage input expects,
 * rounded to two decimals. A fee amount of 0 or missing divides by 1, as before. (OWA-1595)
 */
export function milestonePercentage(amount: number | null | undefined, feeAmount: number | null | undefined): number {
  return toTwoDecimals(100 * (amount ?? 0) / (feeAmount || 1.0));
}

/**
 * A milestone percentage on the 0 to 100 scale as it is frozen into a finalized invoice: a 0 to 1 fraction
 * (25 becomes 0.25), the scale older finalized invoices already hold. Null stays null. (OWA-1595)
 */
export function frozenMilestonePercentage(percentage: number | null | undefined): number | null {
  return percentage == null ? null : Number((percentage / 100).toFixed(6)); // toFixed drops the float noise of the division (66.67 / 100)
}

/** The inverse of frozenMilestonePercentage: a frozen fraction back on the 0 to 100 scale, to two decimals. */
export function liveMilestonePercentage(fraction: number | null | undefined): number | null {
  return fraction == null ? null : toTwoDecimals(fraction * 100);
}

/** The latest completion date among the milestones, or null when none has one. (OWA-1598) */
export function latestCompletionDate(milestones: ReadonlyArray<Dated>): Date | null {
  let latest: Date | null = null;

  for (const m of milestones) {
    const d = m.completionDate;
    if (d && (!latest || new Date(d).getTime() > new Date(latest).getTime())) latest = d;
  }

  return latest;
}

/**
 * Apply the "all milestones earned" rule to a flat fee about to be saved. A fee that is not yet earned
 * becomes earned, dated with the latest milestone completion date (today when no milestone has one). A fee that is already
 * earned keeps the completion date the user chose. Returns true when the fee was changed. (OWA-1598)
 */
export function applyAllMilestonesEarned(
  fee: { earned?: boolean | null, completionDate?: Date | null },
  milestones: ReadonlyArray<Dated>,
  now: Date = new Date(),
): boolean {
  if (fee.earned) return false;
  if (milestones.length === 0 || !milestones.every((m) => m.earned)) return false;

  fee.earned = true;
  fee.completionDate = latestCompletionDate(milestones) ?? now;

  return true;
}

/**
 * The completion date a milestone takes when it is marked earned: the latest completion date among the
 * non-deleted tasks bucketed on the milestone (task.flatFeeMilestoneBucket), or null when there is none.
 * (OWA-1596)
 */
export function milestoneEarnedCompletionDate(
  tasks: ReadonlyArray<{ status?: string | null, flatFeeMilestoneBucket?: string | null, completionDate?: Date | null }>,
  milestoneId: string,
): Date | null {
  return latestCompletionDate(tasks.filter((t) => t.status !== "D").filter((t) => t.flatFeeMilestoneBucket === milestoneId));
}
